import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ALLIANCE_TASK_TITLE_STYLE } from '../../common/allianceTaskOptionsClient'
import type { CastleData } from '../../game/world-map/terrial-engine/types'
import { EspionageV2WorldMap } from '../Espionage/EspionageV2WorldMap'
import { alert } from '../Alert/Alert'

/**
 * Shared pieces for the alliance attack-plan / diplomacy modals: the bronze datetime field and
 * the espionage-map castle/alliance picker. Extracted so the Conquer, Raid, and Diplomacy plan
 * modals all use the exact same widgets.
 */

const AT = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task'
const MODAL_BG = `${AT}/modal-bg.webp`
const BACK_BTN = `${AT}/back-btn.webp`
const CINZEL = '"Cinzel", "Trajan Pro", "Times New Roman", serif'

/** EspionageV2WorldMap self-positions at this fixed rect inside its design stage. */
const MAP_ROOT = { x: 485, y: 150, w: 655, h: 425 }

const CalendarGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4.5" width="18" height="16" rx="2" />
    <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
  </svg>
)
const ClockGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v5l3.2 2" />
  </svg>
)

/** Injected once per field — makes the native datetime picker open on click anywhere in the field. */
const DateTimeFieldStyles: React.FC = () => (
  <style>{`
    .dt-input::-webkit-calendar-picker-indicator { position: absolute; left: 0; top: 0; right: 0; bottom: 0; width: auto; height: auto; margin: 0; padding: 0; opacity: 0; cursor: pointer; }
    .dt-input::-webkit-inner-spin-button, .dt-input::-webkit-clear-button { display: none; -webkit-appearance: none; }
  `}</style>
)

/**
 * Bronze parchment datetime field (calendar icon left, clock icon right). The native picker
 * indicator is stretched invisibly over the field so a click anywhere opens the OS date/time
 * picker; `showPicker()` is a belt-and-suspenders fallback for the icon gutters.
 */
export const DateTimeField: React.FC<{
  value: string
  min: string
  max: string
  disabled?: boolean
  onChange: (v: string) => void
}> = ({ value, min, max, disabled, onChange }) => {
  const ref = useRef<HTMLInputElement>(null)
  const openPicker = () => {
    if (disabled) return
    try {
      ;(ref.current as unknown as { showPicker?: () => void })?.showPicker?.()
    } catch {
      /* not supported — the transparent native indicator still opens it */
    }
  }
  return (
    <div
      onClick={openPicker}
      className="dt-field flex items-center"
      style={{
        height: '9cqw',
        gap: '2cqw',
        padding: '0 2.6cqw',
        cursor: disabled ? 'default' : 'pointer',
        background: 'linear-gradient(180deg, #dccaa0 0%, #c1a165 100%)',
        borderRadius: '1.6cqw',
        border: '0.35cqw solid #9c7a3c',
        boxShadow: 'inset 0 1px 3px rgba(60,40,15,0.35)',
      }}
    >
      <DateTimeFieldStyles />
      <span style={{ color: '#7a5a2e', fontSize: '3.4cqw', display: 'flex', flexShrink: 0 }}>
        <CalendarGlyph />
      </span>
      <input
        ref={ref}
        type="datetime-local"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="dt-input"
        style={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: '#2b1d0c',
          fontFamily: CINZEL,
          fontWeight: 700,
          fontSize: '2.5cqw',
          colorScheme: 'light',
        }}
      />
      <span style={{ color: '#7a5a2e', fontSize: '3.4cqw', display: 'flex', flexShrink: 0 }}>
        <ClockGlyph />
      </span>
    </div>
  )
}

/**
 * Espionage world map embedded in a modal. Clicking a castle returns the full CastleData (owner
 * wallet, alliance id/name, coords). Conquer/Raid use the castle; Diplomacy uses its alliance —
 * the picker itself only enforces "belongs to an enemy alliance (not your own)".
 */
export const CastlePickerModal: React.FC<{
  open: boolean
  address: string
  ownAllianceId?: string
  selectedCastleId?: string | null
  onSelect: (castle: CastleData) => void
  onClose: () => void
  title?: string
  hint?: string
  /** Show only alliance-leader castles (one per alliance) — used by the diplomacy picker. */
  leadersOnly?: boolean
}> = ({ open, address, ownAllianceId, selectedCastleId, onSelect, onClose, title, hint, leadersOnly }) => {
  const { t } = useTranslation()
  const boxRef = useRef<HTMLDivElement>(null)
  const [boxW, setBoxW] = useState(0)

  useEffect(() => {
    if (!open) return
    const el = boxRef.current
    if (!el) return
    const measure = () => setBoxW(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open])

  if (!open || typeof document === 'undefined') return null

  const scale = boxW > 0 ? boxW / MAP_ROOT.w : 0
  const boxH = boxW > 0 ? boxW * (MAP_ROOT.h / MAP_ROOT.w) : 0

  const handleSelect = (castle: CastleData) => {
    if (ownAllianceId && castle.allianceId && castle.allianceId === ownAllianceId) {
      alert.open(t('alliance.conquerNoOwnCastle', { defaultValue: 'You cannot target your own alliance.' }))
      return
    }
    if (!castle.allianceId) {
      alert.open(t('alliance.conquerNeedAlliance', { defaultValue: 'Pick a castle that belongs to an enemy alliance.' }))
      return
    }
    onSelect(castle)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center bg-black/85 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative w-full select-none"
        style={{
          width: 'min(97vw, 960px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '4% 3.5% 1%',
          filter: 'drop-shadow(0 18px 48px rgba(0,0,0,0.7))',
        }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '4.6cqw' }}>
          {title || t('alliance.conquerSelectCastleTitle', { defaultValue: 'Select a Castle' })}
        </h3>
        <p
          className="mb-[2.5cqw] text-center"
          style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.3cqw', color: '#6a4d2c', marginTop: '1cqw' }}
        >
          {hint || t('alliance.conquerSelectCastleHint', { defaultValue: 'Tap an enemy castle on the map to target it.' })}
        </p>

        <div
          ref={boxRef}
          className="relative mx-auto overflow-hidden"
          style={{
            width: '100%',
            height: boxH || 300,
            borderRadius: '2cqw',
            border: '0.5cqw solid rgba(201,162,75,0.6)',
            boxShadow: 'inset 0 0 22px rgba(0,0,0,0.6)',
            background: '#0b1018',
          }}
        >
          {scale > 0 ? (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: MAP_ROOT.w,
                height: MAP_ROOT.h,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <div style={{ position: 'absolute', left: -MAP_ROOT.x, top: -MAP_ROOT.y }}>
                <EspionageV2WorldMap
                  walletAddress={address}
                  selectedCastleId={selectedCastleId ?? null}
                  onSelectCastle={handleSelect}
                  pageScale={scale}
                  hideDecor
                  leadersOnly={leadersOnly}
                />
              </div>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mx-auto mt-[3cqw] block uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99]"
          style={{
            fontFamily: CINZEL,
            fontWeight: 800,
            fontSize: '2.6cqw',
            width: '40%',
            height: '9cqw',
            backgroundImage: `url(${BACK_BTN})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            color: '#f3ead0',
            textShadow: '0 1px 2px rgba(30,15,4,0.85)',
          }}
        >
          {t('common.close', { defaultValue: 'Close' })}
        </button>
      </div>
    </div>,
    document.body,
  )
}
